import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Star, Zap, Crown, Sparkles, Bot, MessageCircle, Coins } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import { useEditableContent } from '@/hooks/useEditableContent';
import { InlineContentEditor } from '@/components/InlineContentEditor';

const PLANS = [
  {
    name: 'Explore',
    price: 'Free',
    priceId: null,
    productId: null,
    description: 'Start your wellness journey',
    features: [
      '14-day trial of all features',
      'Daily check-ins',
      'Basic tracking tools',
      'Oracle cards',
      'Community access',
    ],
    icon: Sparkles,
    isFree: true,
  },
  {
    name: 'School License',
    price: 'Free',
    priceId: 'price_school_placeholder',
    productId: 'prod_school_placeholder',
    description: 'For students aged 13-17 (requires parental/school consent)',
    features: [
      'Oracle Card readings',
      '50 AI credits/month (emergency support)',
      'Daily wellness check-ins',
      'Image uploads only (max 5MB, 10/month)',
      'Cycle & lunar tracking',
      'Quantum journaling (text only)',
      'Nervous system reset',
      'Future self tools',
      'Sleep & dream tracking',
      'Curated meditation library',
    ],
    icon: Sparkles,
    isSchoolLicense: true,
    ageRestricted: true,
    restrictions: {
      maxFileSize: 5, // MB
      maxTotalStorage: 50, // MB
      maxUploadsPerMonth: 10,
      aiCreditsPerMonth: 50,
      noCommunity: true,
      noAlcoholTracking: true,
      noMenopause: true,
      noTelempathy: true,
      noVideoUploads: true,
      noAudioUploads: true,
      coursesRequirePayment: true,
    },
  },
  {
    name: 'Foundations',
    price: '£11.99',
    priceId: 'price_1SEEGHDADiSUiGodWuALEof9',
    productId: 'prod_TAZPXkiLL1pD8u',
    description: '14-day free trial then £11.99/month',
    features: [
      'All Explore features',
      'Full tracking suite',
      'All courses & meditations',
      'Advanced manifestation tools',
      'Vision board creator',
      'Hope reel creator',
      'Crisis support suite',
    ],
    icon: Star,
    popular: true,
  },
  {
    name: 'Mastery',
    price: '£19.99',
    priceId: 'price_1SEEGYDADiSUiGodBhd8Gtgs',
    productId: 'prod_TAZPpPxlUlSDGn',
    description: '14-day trial then £19.99/month',
    features: [
      'Everything in Foundations',
      'Advanced analytics',
      'Priority support',
      'Exclusive content',
      'Early access to new tools',
      'Personalised insights',
      'Premium guidance',
    ],
    icon: Crown,
    popular: false,
  },
];

// Therappi AI 3-Tier System
const THERAPPI_TIERS = [
  {
    id: 'therappi-free',
    name: 'Free Therappi',
    price: 'Free',
    priceId: null,
    productId: null,
    description: 'Try AI Therappi® with limited credits',
    features: [
      '20 non-rolling credits per month',
      '~6 Therappi® conversations',
      'Basic AI chatbot (text-only)',
      'No data integration',
      'Crisis resources'
    ],
    icon: MessageCircle,
    tier: 'free',
    highlight: false
  },
  {
    id: 'therappi-standard',
    name: 'Standard Therappi',
    price: '£12.99',
    priceId: 'price_1SEEH4DADiSUiGod8KwavKx5',
    productId: 'prod_TAZPNU6FJXvArq',
    description: 'Full AI Therappi® access with text conversations',
    features: [
      '300 monthly credits (100 conversations)',
      'Advanced AI Therappi® chatbot',
      'Text-only conversations',
      'End-to-end encrypted',
      'Crisis resources & safety features',
      'Voice add-on available (+£9.99/month)'
    ],
    icon: Bot,
    tier: 'standard',
    highlight: false,
    badge: 'Most Popular'
  },
  {
    id: 'therappi-premium',
    name: 'Premium Therappi',
    price: '£24.99',
    priceId: 'price_NEW_PREMIUM_THERAPPI', // TODO: Create in Stripe
    productId: 'prod_NEW_PREMIUM_THERAPPI', // TODO: Create in Stripe
    description: 'Personalised AI Therappi® with full data integration',
    features: [
      '300 monthly credits (100 conversations)',
      'Personalised AI with your timeline',
      'Daily tracker integration',
      'Past, present & future context',
      'Life events & patterns analysis',
      'Priority support',
      'Voice add-on available (+£9.99/month)'
    ],
    icon: Crown,
    tier: 'premium',
    highlight: true
  }
];

const VOICE_ADDON = {
  id: 'therappi-voice',
  name: 'Voice Add-On',
  price: '+£9.99/month',
  priceId: 'price_NEW_VOICE_ADDON', // TODO: Create in Stripe
  productId: 'prod_NEW_VOICE_ADDON', // TODO: Create in Stripe
  description: 'Add voice input & output to any Therappi tier',
  features: [
    'Voice input (speak to Therappi)',
    'Voice output (Amanda\'s AI voice)',
    'Whisper speech-to-text',
    'ElevenLabs natural voice',
    'Full text history preserved',
    'Works with any tier'
  ],
  icon: Zap,
  compatibleWith: ['free', 'standard', 'premium']
};

const CREDIT_PACKS = [
  {
    name: '100 Credits',
    credits: 100,
    price: '£4.99',
    priceId: 'price_credit_100',
    productId: 'prod_credit_100',
    description: 'Perfect for occasional use',
    savings: null,
  },
  {
    name: '250 Credits',
    credits: 250,
    price: '£9.99',
    priceId: 'price_credit_250',
    productId: 'prod_credit_250',
    description: 'Great value for regular users',
    savings: '£2.50 saved',
    popular: true,
  },
  {
    name: '500 Credits',
    credits: 500,
    price: '£17.99',
    priceId: 'price_credit_500',
    productId: 'prod_credit_500',
    description: 'Best value for heavy users',
    savings: '£7 saved',
  },
  {
    name: '1000 Credits',
    credits: 1000,
    price: '£29.99',
    priceId: 'price_credit_1000',
    productId: 'prod_credit_1000',
    description: 'Maximum value',
    savings: '£20 saved',
    badge: 'Best Deal',
  },
];

interface SubscriptionPlansProps {
  disabled?: boolean;
}

export default function SubscriptionPlans({ disabled = false }: SubscriptionPlansProps) {
  const { 
    isSubscribed, 
    productId, 
    purchasedProducts, 
    monthlyCredits,
    purchasedCredits,
    totalCredits,
    hasAITherappiAccess,
    createCheckout, 
    createPayment, 
    purchaseCredits,
    openCustomerPortal 
  } = useSubscription();

  const handleSubscribe = async (priceId: string) => {
    try {
      const url = await createCheckout(priceId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout session. Please try again.');
    }
  };

  const handlePurchase = async (priceId: string) => {
    try {
      const url = await createPayment(priceId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error('Failed to create payment session. Please try again.');
    }
  };

  const handlePurchaseCredits = async (priceId: string) => {
    try {
      const url = await purchaseCredits(priceId);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error purchasing credits:', error);
      toast.error('Failed to create credit purchase session. Please try again.');
    }
  };

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open subscription management. Please try again.');
    }
  };

  const handleUpgrade = async (priceId: string) => {
    try {
      await openCustomerPortal();
      toast.info('Use the Stripe portal to upgrade your plan');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open upgrade portal. Please try again.');
    }
  };

  const hasAITherappi = purchasedProducts.some(p => 
    p === 'prod_TAZPNU6FJXvArq' || 
    p === 'prod_TAZQgZrKKRf0p0' || 
    p === 'prod_TAZQwkKozLIErj'
  );

  // Editable content hooks
  const pageTitle = useEditableContent('pricing_page_title', 'Choose Your Plan', 'pricing');
  const pageSubtitle = useEditableContent('pricing_page_subtitle', 'Start free with 14-day trial, or upgrade for advanced features', 'pricing');

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="text-center mb-8">
        <InlineContentEditor contentKey="pricing_page_title" category="pricing">
          <h2 className="text-3xl font-bold mb-4">{pageTitle}</h2>
        </InlineContentEditor>
        <InlineContentEditor contentKey="pricing_page_subtitle" category="pricing">
          <p className="text-muted-foreground text-lg">{pageSubtitle}</p>
        </InlineContentEditor>
      </div>

      {/* Subscription Plans */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {PLANS.map((plan) => {
          const Icon = plan.icon;
          const isCurrentPlan = isSubscribed && productId === plan.productId;
          const isFree = plan.isFree;
          
          const planName = useEditableContent(`plan_${plan.name.toLowerCase()}_name`, plan.name, 'pricing');
          const planDesc = useEditableContent(`plan_${plan.name.toLowerCase()}_desc`, plan.description, 'pricing');
          const planPrice = useEditableContent(`plan_${plan.name.toLowerCase()}_price`, plan.price, 'pricing');
          
          return (
            <Card key={plan.name} className={`relative ${plan.popular ? 'border-primary shadow-lg scale-105' : ''}`}>
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  Most Popular
                </Badge>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
                  <Icon className="h-8 w-8 text-primary" />
                </div>
                <InlineContentEditor contentKey={`plan_${plan.name.toLowerCase()}_name`} category="pricing">
                  <CardTitle className="text-2xl">{planName}</CardTitle>
                </InlineContentEditor>
                <InlineContentEditor contentKey={`plan_${plan.name.toLowerCase()}_desc`} category="pricing">
                  <CardDescription className="text-base">{planDesc}</CardDescription>
                </InlineContentEditor>
                <div className="mt-4">
                  <InlineContentEditor contentKey={`plan_${plan.name.toLowerCase()}_price`} category="pricing">
                    <span className="text-4xl font-bold">{planPrice}</span>
                  </InlineContentEditor>
                  {!isFree && <span className="text-muted-foreground">/month</span>}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => {
                    const featureText = useEditableContent(
                      `plan_${plan.name.toLowerCase()}_feature_${index}`, 
                      feature, 
                      'pricing'
                    );
                    return (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <InlineContentEditor 
                          contentKey={`plan_${plan.name.toLowerCase()}_feature_${index}`} 
                          category="pricing"
                        >
                          <span className="text-sm">{featureText}</span>
                        </InlineContentEditor>
                      </li>
                    );
                  })}
                </ul>
                
                <div className="pt-4">
                  {isFree ? (
                    <Button variant="outline" className="w-full" disabled>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Always Free
                    </Button>
                  ) : isCurrentPlan ? (
                    <Button variant="outline" className="w-full" disabled>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Current Plan
                    </Button>
                  ) : isSubscribed ? (
                    <Button 
                      onClick={() => handleUpgrade(plan.priceId!)}
                      className="w-full"
                      size="lg"
                      variant={plan.name === 'Mastery' ? 'default' : 'outline'}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      {plan.name === 'Mastery' ? 'Upgrade to Mastery' : 'Switch to Foundations'}
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => handleSubscribe(plan.priceId!)}
                      className="w-full"
                      size="lg"
                    >
                      Start 14-Day Free Trial
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Therappi AI Tiers Section */}
      <div className="mb-8">
        <InlineContentEditor contentKey="therappi_section_title" category="pricing">
          <h3 className="text-2xl font-bold text-center mb-2">
            {useEditableContent('therappi_section_title', 'Therappi® AI - Choose Your Tier', 'pricing')}
          </h3>
        </InlineContentEditor>
        <InlineContentEditor contentKey="therappi_section_subtitle" category="pricing">
          <p className="text-muted-foreground text-center mb-6">
            {useEditableContent('therappi_section_subtitle', 'Advanced AI Therappi® powered by Claude Sonnet 4.5. Optional voice add-on available.', 'pricing')}
          </p>
        </InlineContentEditor>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {THERAPPI_TIERS.map((tier) => {
          const Icon = tier.icon;
          const isCurrentTier = tier.tier === 'free'; // TODO: Get from user's actual tier
          
          return (
            <Card 
              key={tier.id} 
              className={`relative ${
                tier.highlight ? 'border-primary shadow-lg' : 'border-secondary/20'
              } ${isCurrentTier ? 'border-primary bg-primary/5' : ''}`}
            >
              {tier.highlight && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-primary to-secondary">
                  Best Value
                </Badge>
              )}
              {tier.badge && !tier.highlight && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
                  {tier.badge}
                </Badge>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
                  <Icon className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">{tier.name}</CardTitle>
                <CardDescription className="text-base">{tier.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-3xl font-bold">{tier.price}</span>
                  {tier.price !== 'Free' && <span className="text-muted-foreground">/month</span>}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => tier.priceId && handleSubscribe(tier.priceId)}
                  className="w-full"
                  disabled={disabled || isCurrentTier || !tier.priceId}
                  variant={tier.highlight ? "default" : "outline"}
                >
                  {isCurrentTier ? 'Current Plan' : (tier.priceId ? 'Subscribe' : 'Free Plan')}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Voice Add-On Card */}
      <Card className="border-accent bg-accent/5 mb-12">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-accent/10">
              <Zap className="h-8 w-8 text-accent" />
            </div>
            <div>
              <CardTitle className="text-xl">{VOICE_ADDON.name}</CardTitle>
              <CardDescription>{VOICE_ADDON.description}</CardDescription>
            </div>
          </div>
          <div className="mt-4">
            <span className="text-2xl font-bold text-accent">{VOICE_ADDON.price}</span>
            <p className="text-sm text-muted-foreground mt-1">Add to any Therappi tier</p>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            {VOICE_ADDON.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
          <Button
            onClick={() => handleSubscribe(VOICE_ADDON.priceId)}
            className="w-full"
            disabled={disabled}
            variant="default"
          >
            <Zap className="mr-2 h-4 w-4" />
            Add Voice to My Plan
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-3">
            Requires ElevenLabs account for development (Free tier OK). Pro plan needed for voice cloning in production.
          </p>
        </CardContent>
      </Card>

      {/* Credit Packs Section */}
      {hasAITherappiAccess() && (
        <>
          <div className="mb-8 mt-12">
            <InlineContentEditor contentKey="credits_section_title" category="pricing">
              <h3 className="text-2xl font-bold text-center mb-2 flex items-center justify-center gap-2">
                <Coins className="h-6 w-6 text-primary" />
                {useEditableContent('credits_section_title', 'AI Therappi® Credits', 'pricing')}
              </h3>
            </InlineContentEditor>
            <InlineContentEditor contentKey="credits_section_subtitle" category="pricing">
              <p className="text-muted-foreground text-center mb-2">
                {useEditableContent('credits_section_subtitle', 'Top up your credits for unlimited AI conversations', 'pricing')}
              </p>
            </InlineContentEditor>
            {totalCredits !== undefined && (
              <div className="text-center">
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  Current Balance: {totalCredits} credits
                </Badge>
                <p className="text-sm text-muted-foreground mt-2">
                  Monthly: {monthlyCredits} | Purchased: {purchasedCredits}
                </p>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-8">
            {CREDIT_PACKS.map((pack) => {
              const packKey = pack.name.toLowerCase().replace(/\s+/g, '_');
              const packName = useEditableContent(`credit_${packKey}_name`, pack.name, 'pricing');
              const packPrice = useEditableContent(`credit_${packKey}_price`, pack.price, 'pricing');
              const packDesc = useEditableContent(`credit_${packKey}_desc`, pack.description, 'pricing');
              const packCredits = useEditableContent(`credit_${packKey}_credits`, pack.credits.toString(), 'pricing');
              
              return (
                <Card key={pack.name} className={`relative ${pack.popular ? 'border-primary shadow-md' : ''}`}>
                  {pack.badge && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <InlineContentEditor contentKey={`credit_${packKey}_badge`} category="pricing">
                        {useEditableContent(`credit_${packKey}_badge`, pack.badge, 'pricing')}
                      </InlineContentEditor>
                    </Badge>
                  )}
                  {pack.popular && !pack.badge && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2" variant="secondary">
                      Popular
                    </Badge>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <div className="mx-auto mb-2 p-2 rounded-full bg-primary/10 w-fit">
                      <Coins className="h-6 w-6 text-primary" />
                    </div>
                    <InlineContentEditor contentKey={`credit_${packKey}_name`} category="pricing">
                      <CardTitle className="text-xl">{packName}</CardTitle>
                    </InlineContentEditor>
                    <div className="mt-2">
                      <InlineContentEditor contentKey={`credit_${packKey}_price`} category="pricing">
                        <span className="text-3xl font-bold">{packPrice}</span>
                      </InlineContentEditor>
                    </div>
                    <InlineContentEditor contentKey={`credit_${packKey}_desc`} category="pricing">
                      <CardDescription className="text-sm mt-1">{packDesc}</CardDescription>
                    </InlineContentEditor>
                    {pack.savings && (
                      <Badge variant="outline" className="mt-2 text-green-600 border-green-600">
                        <InlineContentEditor contentKey={`credit_${packKey}_savings`} category="pricing">
                          {useEditableContent(`credit_${packKey}_savings`, pack.savings, 'pricing')}
                        </InlineContentEditor>
                      </Badge>
                    )}
                  </CardHeader>
                
                  <CardContent className="space-y-4">
                    <div className="text-center py-4">
                      <InlineContentEditor contentKey={`credit_${packKey}_credits`} category="pricing">
                        <p className="text-2xl font-bold text-primary">{packCredits}</p>
                      </InlineContentEditor>
                      <p className="text-sm text-muted-foreground">conversation exchanges</p>
                      <p className="text-xs text-muted-foreground mt-1">Never expire</p>
                    </div>
                  
                    <Button 
                      onClick={() => handlePurchaseCredits(pack.priceId)}
                      className="w-full"
                      variant={pack.popular ? 'default' : 'outline'}
                      disabled={disabled}
                    >
                      Purchase Credits
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Active Subscription Card */}
      {isSubscribed && (
        <Card className="bg-gradient-to-r from-primary/5 to-secondary/5">
          <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-primary" />
              <div>
                <h3 className="font-semibold">
                  {productId === 'prod_TAZPpPxlUlSDGn' ? 'Mastery Active!' : 
                   productId === 'prod_TAZPXkiLL1pD8u' ? 'Foundations Active!' : 
                   'Subscription Active!'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {hasAITherappi ? 'AI Therappi® is active ✨' : 'Add AI Therappi® for personalised guidance'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {productId === 'prod_TAZPXkiLL1pD8u' && (
                <Button onClick={() => handleUpgrade(PLANS[2].priceId!)} variant="default">
                  <Zap className="h-4 w-4 mr-2" />
                  Upgrade to Mastery
                </Button>
              )}
              <Button onClick={handleManageSubscription} variant="outline">
                Manage Subscription
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
